// Private insurance, manual workflow (docs/plans/phase2-private-insurance.md §4, §6).
import { queryOptions } from '@tanstack/react-query';
import {
  benefitRuleListSchema,
  benefitRuleViewSchema,
  coverageListSchema,
  coverageViewSchema,
  debtorListSchema,
  insurerClaimDetailSchema,
  insurerClaimListSchema,
  insurerInvoiceDetailSchema,
  insurerInvoiceListSchema,
  insurerPreauthViewSchema,
  openInsurerClaimListSchema,
  remittanceDetailSchema,
  remittanceListSchema,
  visitInsuranceSchema,
  type AmountView,
  type InsurerClaimListView,
  type RecordCoverageRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export const insuranceKeys = {
  all: ['insurance'] as const,
  covers: (patientId: string) => ['patients', patientId, 'coverages'] as const,
  rules: (schemeId: string) => ['insurance', 'rules', schemeId] as const,
  visit: (visitId: string) => ['visits', visitId, 'insurance'] as const,
  claims: (view: InsurerClaimListView) => ['insurance', 'claims', view] as const,
  claim: (id: string) => ['insurance', 'claim', id] as const,
  invoices: ['insurance', 'invoices'] as const,
  invoice: (id: string) => ['insurance', 'invoice', id] as const,
  remittances: ['insurance', 'remittances'] as const,
  remittance: (id: string) => ['insurance', 'remittance', id] as const,
  openClaims: (payerId: string) => ['insurance', 'open-claims', payerId] as const,
  debtors: ['insurance', 'debtors'] as const,
  preauths: ['insurance', 'preauths'] as const,
};

// --- Cover -----------------------------------------------------------------------------

export const coveragesQuery = (patientId: string) =>
  queryOptions({
    queryKey: insuranceKeys.covers(patientId),
    queryFn: () => api('GET', `/patients/${patientId}/coverages`, { schema: coverageListSchema }),
  });

export function recordCoverage(patientId: string, body: RecordCoverageRequest) {
  return api('POST', `/patients/${patientId}/coverages`, { body, schema: coverageViewSchema });
}

export function changeCoverage(
  id: string,
  to: 'end' | 'entered-in-error',
  body: { expectedVersion: number; reason: string },
) {
  return api('POST', `/coverages/${id}/${to}`, { body, schema: coverageViewSchema });
}

// --- Benefit rules ---------------------------------------------------------------------

export const benefitRulesQuery = (schemeId: string) =>
  queryOptions({
    queryKey: insuranceKeys.rules(schemeId),
    queryFn: () =>
      api('GET', `/schemes/${schemeId}/benefit-rules`, { schema: benefitRuleListSchema }),
  });

export function createBenefitRule(
  schemeId: string,
  body: {
    kind: 'limit' | 'excess' | 'preauth_required';
    period: 'per_visit' | 'per_year' | null;
    category: string | null;
    amount: AmountView;
  },
) {
  return api('POST', `/schemes/${schemeId}/benefit-rules`, { body, schema: benefitRuleViewSchema });
}

export function deactivateBenefitRule(
  id: string,
  body: { expectedVersion: number; reason: string },
) {
  return api('POST', `/benefit-rules/${id}/deactivate`, { body, schema: benefitRuleViewSchema });
}

// --- The visit -------------------------------------------------------------------------

export const visitInsuranceQuery = (visitId: string) =>
  queryOptions({
    queryKey: insuranceKeys.visit(visitId),
    queryFn: () => api('GET', `/visits/${visitId}/insurance`, { schema: visitInsuranceSchema }),
  });

export function recordPreauth(
  visitId: string,
  body: { serviceItemId: string | null; requested: AmountView | null; note?: string },
) {
  return api('POST', `/visits/${visitId}/insurer-preauths`, {
    body,
    schema: insurerPreauthViewSchema,
  });
}

export function decidePreauth(
  id: string,
  body:
    | {
        outcome: 'approved';
        expectedVersion: number;
        insurerReference: string;
        approved: AmountView;
        validUntil?: string;
      }
    | { outcome: 'declined'; expectedVersion: number; reason: string; insurerReference?: string },
) {
  return api('POST', `/insurer-preauths/${id}/decision`, {
    body,
    schema: insurerPreauthViewSchema,
  });
}

export const preauthsQuery = queryOptions({
  queryKey: insuranceKeys.preauths,
  queryFn: () =>
    api('GET', '/insurer-preauths', {
      schema: z.object({ items: z.array(insurerPreauthViewSchema) }),
    }),
});

// --- Claims and invoices -----------------------------------------------------------------

export const insurerClaimsQuery = (view: InsurerClaimListView) =>
  queryOptions({
    queryKey: insuranceKeys.claims(view),
    queryFn: () => api('GET', `/insurer-claims?view=${view}`, { schema: insurerClaimListSchema }),
  });

export const insurerClaimQuery = (id: string) =>
  queryOptions({
    queryKey: insuranceKeys.claim(id),
    queryFn: () => api('GET', `/insurer-claims/${id}`, { schema: insurerClaimDetailSchema }),
  });

export function prepareInsurerClaim(visitId: string) {
  return api('POST', `/visits/${visitId}/insurer-claims`, {
    schema: z.object({ claimId: z.uuid() }),
  });
}

export function issueInvoice(body: {
  payerId: string;
  claims: { id: string; expectedVersion: number }[];
}) {
  return api('POST', '/insurer-invoices', { body, schema: z.object({ invoiceId: z.uuid() }) });
}

export function recordRejection(id: string, body: { expectedVersion: number; reason: string }) {
  return api('POST', `/insurer-claims/${id}/rejection`, { body });
}

export function resubmit(id: string, expectedVersion: number) {
  return api('POST', `/insurer-claims/${id}/resubmit`, { body: { expectedVersion } });
}

export function markPrinted(id: string) {
  return api('POST', `/insurer-claims/${id}/print`);
}

export function askWriteOff(
  id: string,
  body: { amount: AmountView; reasonCode: string; reasonNote?: string },
) {
  return api('POST', `/insurer-claims/${id}/write-offs`, { body });
}

export function decideWriteOff(id: string, action: 'approve' | 'reject', note?: string) {
  return api('POST', `/insurer-claim-write-offs/${id}/${action}`, {
    body: note === undefined ? {} : { note },
    schema: z.object({ claimId: z.uuid() }),
  });
}

export const invoicesQuery = queryOptions({
  queryKey: insuranceKeys.invoices,
  queryFn: () => api('GET', '/insurer-invoices', { schema: insurerInvoiceListSchema }),
});

export const invoiceQuery = (id: string) =>
  queryOptions({
    queryKey: insuranceKeys.invoice(id),
    queryFn: () => api('GET', `/insurer-invoices/${id}`, { schema: insurerInvoiceDetailSchema }),
  });

// --- Payments and debtors ------------------------------------------------------------------

export const remittancesQuery = queryOptions({
  queryKey: insuranceKeys.remittances,
  queryFn: () => api('GET', '/insurer-remittances', { schema: remittanceListSchema }),
});

export const remittanceQuery = (id: string) =>
  queryOptions({
    queryKey: insuranceKeys.remittance(id),
    queryFn: () => api('GET', `/insurer-remittances/${id}`, { schema: remittanceDetailSchema }),
  });

export const openClaimsQuery = (payerId: string) =>
  queryOptions({
    queryKey: insuranceKeys.openClaims(payerId),
    queryFn: () =>
      api('POST', '/insurer-claims/open', {
        body: { payerId },
        schema: openInsurerClaimListSchema,
      }),
  });

export function recordRemittance(body: {
  payerId: string;
  reference: string;
  method: 'eft' | 'cheque' | 'mpesa' | 'other';
  receivedOn: string;
  amount: AmountView;
  note?: string;
  allocations: { claimId: string; amount: AmountView }[];
}) {
  return api('POST', '/insurer-remittances', {
    body,
    schema: z.object({ remittanceId: z.uuid() }),
  });
}

export function allocateRemittance(
  id: string,
  allocations: { claimId: string; amount: AmountView }[],
) {
  return api('POST', `/insurer-remittances/${id}/allocations`, { body: { allocations } });
}

export const debtorsQuery = queryOptions({
  queryKey: insuranceKeys.debtors,
  queryFn: () => api('GET', '/insurer-debtors', { schema: debtorListSchema }),
});
